import { adminRecords } from "./adminRecords";
import { useEffect, useMemo, useState } from "react";
import update from "react-addons-update";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";
import Select from "react-select";
import Card from "../models/Card";
import { SOURCES } from "../Utils";
import CardSetView from "./CardSetView";
import CopyToClipboardButton from "./CopyToClipboardButton.tsx";

let FILTER_SOURCES = SOURCES.sort().map((source) => {
  return { value: source, label: source };
});

function CardSets(props) {
  const [results, setResults] = useState([]);
  const [cards, setCards] = useState([]);
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setPage] = useState(1);
  const [source, setSource] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const showUserColumn = props.showUserColumn !== undefined ? props.showUserColumn : true;

  async function query(page, clear = false) {
    const ref = adminRecords.collection("cards");
    let query;
    let set = {};

    if (queries[page] && !clear) {
      query = queries[page];
    } else {
      query = ref.limit(25);
      query = query.orderBy("createdAt", "desc");

      if (source) {
        query = query.where("source", "==", source.value);
      }
      if (startDate) {
        query = query.where("createdAt", ">=", new Date(`${startDate}T00:00:00Z`));
      }
      if (endDate) {
        query = query.where("createdAt", "<=", new Date(`${endDate}T23:59:59Z`));
      }

      if (page != 1) {
        // look at previous page to build next query
        let result = results[page - 1];

        if (result) {
          query = query.startAfter(result[result.length - 1]);
        } else {
          console.log("Cannot construct a query for a page that is not adjacent to one already fetched");
        }
      }

      setQueries(update(queries, { [page]: { $set: query } }));
    }

    if (props.user) {
      query = query.where("userId", "==", props.user.uid);
    }

    let querySnapshot = await query.get();

    set.completions = querySnapshot.docs;

    if (showUserColumn && props.userCache) {
      await props.userCache.fetchIds(querySnapshot.docs.map((s) => s.get("userId")));
    }

    setCards(querySnapshot.docs);
    setResults(update(results, { [page]: { $set: querySnapshot.docs } }));
    setLoading(false);
  }

  useEffect(() => {
    query(currentPage);
  }, [currentPage]);

  useEffect(() => {
    setQueries([]);
    setPage(1);
    query(1, true);
  }, [source, startDate, endDate]);

  const columns = useMemo(() => {
    const cols = [
      {
        name: "ID",
        width: "220px",
        selector: (row) => {
          return row.id;
        },
        sortable: false,
        cell: (row) => (
          <CopyToClipboardButton
            textToCopy={row.id}
            successMessage={`Copied ID to clipboard: ${row.id}`}
            className="font-mono w-full"
          />
        ),
      },
      {
        name: "Date",
        width: "160px",
        selector: (row) => {
          return row.get("createdAt").toDate().toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
        },
        sortable: false,
        cell: (row) => (
          <div className="text-sm text-gray-600">
            {row.get("createdAt").toDate().toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        ),
      },
      {
        name: "Type",
        width: "200px",
        selector: (row) => {
          if (row.get("guidance")) {
            return "Guided Wormhole";
          } else {
            return Card.name(row.get("source"));
          }
        },
        sortable: false,
        cell: (row) => {
          const type = row.get("guidance") ? "Guided Wormhole" : Card.name(row.get("source"));
          return <div className="text-sm font-medium overflow-hidden text-ellipsis whitespace-nowrap">{type}</div>;
        },
      },
      {
        name: "Prompt",
        width: "250px",
        selector: (row) => {
          const prompt = row.get(Card.summaryKey(row.get("source")));
          if (!prompt) {
            return "";
          } else if (prompt.slice) {
            return prompt.slice(0, 50) + "...";
          } else {
            return JSON.stringify(prompt);
          }
        },
        sortable: false,
        cell: (row) => {
          const prompt = row.get(Card.summaryKey(row.get("source")));
          if (!prompt) {
            return <div className="text-gray-500 italic">No prompt</div>;
          }

          let displayPrompt;
          if (prompt.slice) {
            displayPrompt = prompt.slice(0, 50) + "...";
          } else {
            displayPrompt = JSON.stringify(prompt).slice(0, 50) + "...";
          }

          return <div className="text-sm text-gray-800 leading-tight overflow-hidden text-ellipsis whitespace-nowrap">{displayPrompt}</div>;
        },
      },
    ];

    if (showUserColumn) {
      cols.splice(2, 0, {
        name: "User",
        width: "200px",
        selector: (row) => {
          const user = props.userCache?.getUser(row.get("userId"));
          return user ? user.displayName || user.email : "User Deleted";
        },
        sortable: false,
        cell: (row) => {
          const user = props.userCache?.getUser(row.get("userId"));
          if (user) {
            return (
              <Link
                to={"/admin/user/" + user.email}
                className="text-app-primary hover:underline transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                {user.displayName || user.email}
              </Link>
            );
          }
          return <span className="text-gray-500 italic">User Deleted</span>;
        },
      });
    }

    return cols;
  }, [props.userCache, showUserColumn]);

  const ExpandableComponent = ({ data }) => {
    return (
      <div className="border-l-4 bg-gray-50 p-6 mx-4 my-2 rounded-r-lg">
        <CardSetView data={data} />
      </div>
    );
  };

  return (
    <div className="p-4">
      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <Select
          className="w-full max-w-md"
          value={source}
          onChange={(selectedOption) => {
            setSource(selectedOption);
          }}
          options={FILTER_SOURCES}
          placeholder="Filter by source..."
          isClearable
        />
        <input
          type="date"
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-app-primary-cta focus:border-app-primary-cta"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          title="Start date (UTC)"
          aria-label="Start date"
        />
        <input
          type="date"
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-app-primary-cta focus:border-app-primary-cta"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          title="End date (UTC)"
          aria-label="End date"
        />
        {(startDate || endDate) && (
          <button
            type="button"
            className="text-xs text-gray-600 hover:text-gray-900 underline"
            onClick={() => {
              setStartDate("");
              setEndDate("");
            }}
          >
            Clear dates
          </button>
        )}
      </div>
      <DataTable
        columns={columns}
        responsive={true}
        progressPending={loading}
        expandableRows
        expandableRowsComponent={<ExpandableComponent />}
        pagination
        paginationServer
        paginationPerPage={25}
        paginationRowsPerPageOptions={[25]}
        paginationTotalRows={99999999}
        onChangePage={(page) => {
          setPage(page);
        }}
        data={cards}
      />
    </div>
  );
}

export default CardSets;
